import { type ReactNode } from "react";
import { CheckCircleIcon, RemoveCircleIcon } from "vega-icons";

import { cnTwMerge } from "@vega-core/utils/cnTwMerge";

import { Badge } from "../shadcn/badge";

export type TagProps = {
  children: ReactNode;
  isLoading?: boolean;
  dataTestId?: string;
  variant?: TagStyleKeys;
  dotVariant?: TagDotStyleKeys;
  wMin?: boolean;
};

export type TagStyleKeys = keyof typeof TagVariants;
export type TagDotStyleKeys = keyof typeof DotVariant;

export enum TagVariants {
  Default = "Default",
  Positive = "Positive",
  Negative = "Negative",
  Pending = "Pending",
  Warning = "Warning",
  Disabled = "Disabled",
  Data1 = "Data1",
  Data2 = "Data2",
  Data3 = "Data3",
  Data4 = "Data4",
  Data5 = "Data5",
  Regular = "Regular",
  Gold = "Gold",
  SLADefault = "SLADefault",
  SLAWarning = "SLAWarning",
  SLANegative = "SLANegative",
  Tertiary = "Tertiary",
}

const TagVariantMap = {
  Default: {
    tagClassName: "bg-background/component/10",
    dotClassName: "bg-text/primary",
    iconClassName: "text-text/primary",
  },
  Positive: {
    tagClassName: "bg-text/positive/20",
    dotClassName: "bg-text/positive",
    iconClassName: "text-text/positive",
  },
  Negative: {
    tagClassName: "bg-text/negative/20",
    dotClassName: "bg-text/negative",
    iconClassName: "text-text/negative",
  },
  Pending: {
    tagClassName: "bg-text/pending/20",
    dotClassName: "bg-text/pending",
    iconClassName: "text-text/pending",
  },
  Warning: {
    tagClassName: "bg-text/warning/20",
    dotClassName: "bg-text/warning",
    iconClassName: "text-text/warning",
  },
  Disabled: {
    tagClassName: "bg-text/disabled/20 text-text/secondary",
    dotClassName: "bg-text/disabled",
    iconClassName: "text-text/disabled",
  },
  Data1: {
    tagClassName: "bg-data-1/20",
    dotClassName: "bg-data-1",
    iconClassName: "text-data-1",
  },
  Data2: {
    tagClassName: "bg-data-2/20",
    dotClassName: "bg-data-2",
    iconClassName: "text-data-2",
  },
  Data3: {
    tagClassName: "bg-data-3/20",
    dotClassName: "bg-data-3",
    iconClassName: "text-data-3",
  },
  Data4: {
    tagClassName: "bg-data-4/20",
    dotClassName: "bg-data-4",
    iconClassName: "text-data-4",
  },
  Data5: {
    tagClassName: "bg-data-5/20",
    dotClassName: "bg-data-5",
    iconClassName: "text-data-5",
  },
  Regular: {
    tagClassName:
      "bg-background/primary text-text/secondary border-1 border-border/primary",
    dotClassName: "bg-text/primary",
    iconClassName: "text-text/primary",
  },
  Gold: {
    tagClassName: "bg-gold-default/20",
    dotClassName: "bg-gold-default",
    iconClassName: "text-gold-default",
  },
  SLADefault: {
    tagClassName:
      "bg-background/primary text-text/primary border-1 border-border/primary",
    dotClassName: "bg-text/primary",
    iconClassName: "text-text/primary",
  },
  SLAWarning: {
    tagClassName: "bg-text/warning/10 text-text/warning",
    dotClassName: "bg-text/warning",
    iconClassName: "text-text/warning",
  },
  SLANegative: {
    tagClassName: "bg-text/negative/10 text-text/negative",
    dotClassName: "bg-text/negative",
    iconClassName: "text-text/negative",
  },
  Tertiary: {
    tagClassName: "bg-background/tertiary/20",
    dotClassName: "bg-background/tertiary",
    iconClassName: "text-background/tertiary",
  },
};

export enum DotVariant {
  CHECK = "CHECK",
  REMOVED = "REMOVED",
  DEFAULT = "DEFAULT",
}

const getStatusDot = (status: TagDotStyleKeys, style: TagStyleKeys) => {
  switch (status) {
    case DotVariant.CHECK:
      return (
        <CheckCircleIcon
          className={cnTwMerge(
            "w-4 h-4 min-w-4 min-h-4",
            TagVariantMap[style].iconClassName,
          )}
        />
      );
    case DotVariant.REMOVED:
      return (
        <RemoveCircleIcon
          className={cnTwMerge(
            "w-4 h-4 min-w-4 min-h-4",
            TagVariantMap[style].iconClassName,
          )}
        />
      );
    case DotVariant.DEFAULT:
      return (
        <div
          className={cnTwMerge(
            "w-2 h-2 min-w-2 min-h-2 rounded-full",
            TagVariantMap[style].dotClassName,
          )}
        />
      );
  }
};

export const Tag = ({
  children,
  isLoading = false,
  dataTestId = "tag",
  variant = "Default",
  dotVariant,
  wMin = false,
}: TagProps) => (
  <Badge
    className={cnTwMerge(
      "h-min body-small-semibold inline-flex items-center w-fit min-w-fit gap-1 px-3 py-1 whitespace-nowrap text-nowrap defaultTransition",
      "rounded-small",
      TagVariantMap[variant].tagClassName,
      isLoading && "skeleton-box w-24",
      { "w-min": wMin },
    )}
    data-testid={dataTestId}
  >
    {dotVariant ? getStatusDot(dotVariant, variant) : null}
    {children}
  </Badge>
);
